import { Controller } from "@hotwired/stimulus"
import sortable from "html5sortable/dist/html5sortable.es"

// Manages the scouting report template builder: sections, fields, drag-drop, and serialization.
export default class extends Controller {
  static targets = ["structureInput", "sectionsContainer", "section", "fieldsContainer", "field"]
  static values = { fieldTypes: Object }

  connect() {
    this.initSortable()
  }

  // ── Sections ──────────────────────────────────────────────────────────────

  addSection() {
    const template = document.getElementById("section-template")
    const clone = template.content.cloneNode(true)
    this.sectionsContainerTarget.appendChild(clone)
    this.initSortable()
  }

  removeSection(event) {
    const section = event.target.closest("[data-scouting-template-builder-target='section']")
    if (section) section.remove()
  }

  toggleSection(event) {
    const section = event.currentTarget.closest("[data-scouting-template-builder-target='section']")
    const bodies = section.querySelectorAll("[data-section-body]")
    const chevron = section.querySelector("[data-section-chevron]")
    const collapsed = bodies[0].hidden
    bodies.forEach(el => el.hidden = !collapsed)
    chevron.style.transform = collapsed ? '' : 'rotate(-90deg)'
  }

  // ── Fields ────────────────────────────────────────────────────────────────

  addField(event) {
    const section = event.target.closest("[data-scouting-template-builder-target='section']")
    const container = section.querySelector("[data-scouting-template-builder-target='fieldsContainer']")
    const template = document.getElementById("field-template")
    const clone = template.content.cloneNode(true)
    container.appendChild(clone)
    this.initSortable()
  }

  removeField(event) {
    const field = event.target.closest("[data-scouting-template-builder-target='field']")
    if (field) field.remove()
  }

  // ── Field type change ─────────────────────────────────────────────────────

  changeFieldType(event) {
    const fieldEl = event.target.closest("[data-scouting-template-builder-target='field']")
    const selectedType = event.target.value
    fieldEl.dataset.fieldType = selectedType

    const typeConfig = this.fieldTypesValue[selectedType]
    const optionKeys = typeConfig ? Object.keys(typeConfig.options || {}) : []

    // Show/hide config panels based on selected type
    fieldEl.querySelectorAll("[data-config-type]").forEach((panel) => {
      panel.hidden = !optionKeys.includes(panel.dataset.configType)
    })

    // Hide the whole options section if no options apply to this type
    const optionsSection = fieldEl.querySelector("[data-options-section]")
    if (optionsSection) optionsSection.hidden = optionKeys.length === 0
  }

  // ── Serialization ─────────────────────────────────────────────────────────

  serializeStructure(event) {
    const sections = []

    this.sectionTargets.forEach((sectionEl) => {
      const titleInput = sectionEl.querySelector("[data-field='title']")
      const fields = []

      sectionEl.querySelectorAll("[data-scouting-template-builder-target='field']").forEach((fieldEl) => {
        // Reuse existing field ID if present (critical for edit — existing reports
        // reference fields by UUID in their answers hash). Only generate a new ID
        // for freshly added fields that don't have one yet.
        const field = {
          id: fieldEl.dataset.fieldId || this.generateId(),
          name: fieldEl.querySelector("[data-field='name']")?.value || "",
          type: fieldEl.querySelector("[data-field='type']")?.value || "text_field",
          description: fieldEl.querySelector("[data-field='description']")?.value || "",
          required: fieldEl.querySelector("[data-field='required']")?.checked || false,
          config: {}
        }

        // Collect config values
        fieldEl.querySelectorAll("[data-config-key]").forEach((configInput) => {
          const key = configInput.dataset.configKey
          const panel = configInput.closest("[data-config-type]")
          if (!panel.hidden && configInput.value) {
            field.config[key] = configInput.value
          }
        })

        fields.push(field)
      })

      sections.push({
        id: sectionEl.dataset.sectionId || this.generateId(),
        title: titleInput?.value || "",
        fields: fields
      })
    })

    this.structureInputTarget.value = JSON.stringify({ sections: sections })
  }

  // ── Drag & drop ───────────────────────────────────────────────────────────

  // html5sortable only marks the drag handles present at init as draggable, so
  // every container is re-initialized whenever a section or field is added —
  // calling sortable() again resets its bindings on its own.
  initSortable() {
    sortable(this.sectionsContainerTarget, {
      items: "[data-scouting-template-builder-target='section']",
      handle: "[data-drag-handle='section']",
      placeholder: '<div class="border-2 border-dashed border-base-300 rounded-xl p-4 my-2"></div>'
    })

    this.fieldsContainerTargets.forEach((container) => {
      sortable(container, {
        items: "[data-scouting-template-builder-target='field']",
        handle: "[data-drag-handle='field']",
        placeholder: '<div class="border-2 border-dashed border-base-300 rounded-lg p-2 my-1"></div>',
        acceptFrom: "[data-scouting-template-builder-target='fieldsContainer']"
      })
    })
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  generateId() {
    return crypto.randomUUID()
  }
}
